import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import {
  type Disclosure,
  getMicrositeConfig,
  type MicrositeConfig,
  type SectionsConfig,
} from 'api/MicrositeConfig/getMicrositeConfig';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import { Microsites } from 'constants/constants';
import type { HeaderNav } from 'interfaces/navigation/Header';
import getMenu from 'api/Header/getMenu';
import { recommendsLogo } from 'icons/RecommendsLogo';
import { setPianoConfig } from 'services/Piano';
import { populateThumbnailToMenuItems } from 'api/Topic/getTopicMenuItemThumbnails';
import SchoolPage from 'pages/education/SchoolPage';
import getEduInstitutionBySlug from 'api/EduRanking/getInstitution';
import { setHeadData } from 'utils/metaDataUtils/headData';
import type { EduAdWigetsByPosition, EduInstitution, EduRankingList } from 'interfaces/EduRanking';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import EduRankingPage from 'pages/education/RankingPage';
import getEduRankingListBySlug, { checkEduRankingListBySlug } from 'api/EduRanking/getEduRankingList';
import { ArticlePage } from 'pages/Article/V1/ArticlePage';
import RightRailNavigation, { type RightRailNavigationProps } from 'components/RightRailNavigation';
import SidebarPaidPlacement from 'pages/Article/V1/SidebarPaidPlacement';
import PaidPlacement, { AD_TAGS } from 'components/PaidPlacement';
import { LandingPage } from 'pages/LandingPage';
import type { Article, ArticlePreview } from 'interfaces/content/articles/Articles';
import getCommonProps, { getArticlePaths } from 'utils/miscUtils/articlePageHelpers';
import type { PreviewData } from 'interfaces/PreviewData';
import { parseHeaderForRightRailNavigation } from 'utils/parsingUtils/serverSideParsing';
import getAdWigetsByAdTag from 'api/EduRanking/getAdWidgets';
import { type Page, PageType } from 'interfaces/content/articles/Post';
import getSection from 'api/Section/getSection';
import type { Hub } from 'interfaces/Home';
import getFooter from 'api/Footer/getFooter';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallback from 'lib/fallbackData/commonData';
import recommendsFallback from 'lib/fallbackData/recommendsData';
import type { GtmPage } from 'services/Gtm/entities';
import type { EduRankingPageMeta } from 'interfaces/content/EducationRanking';
import EDUCATION_ADS_TEST_IDS from 'constants/testsIds/educationAds';
import setEducationPageAdConfig from 'services/Ad/config/setEducationPageAdConfig';
import type { JSX } from 'react';
import type { Breadcrumb } from 'interfaces/navigation/Breadcrumbs';
import { PianoExperiencePageType } from '@/app_lib/types/piano/Paywall';

interface PageProps {
  institution: EduInstitution;
  rankingList: EduRankingList;
  article: Article;
  mostPopular: ArticlePreview[];
  pageType: PageType;
  rightRailNavigation: RightRailNavigationProps;
  adWidgets: EduAdWigetsByPosition;
}

const EduArticleAndRankingListAndSchool: NextPage<PageProps> = ({
  institution,
  rankingList,
  pageType,
  article,
  mostPopular,
  rightRailNavigation,
  adWidgets,
}: PageProps) => {
  switch (pageType) {
    case PageType.ARTICLE: {
      const postId = article.postId.toString();
      const utmCampaign = article.primarySection.name === 'Careers Hub' ? 'careers' : 'articles';
      let articleEmbeds: { component: JSX.Element; index: number; node: string }[] = [];
      if (adWidgets && adWidgets.in_stream && adWidgets.in_stream.length > 0) {
        articleEmbeds = adWidgets.in_stream.slice(0, 2).map((ad, adIndex) => ({
          component: (
            <PaidPlacement
              dataCy={`${EDUCATION_ADS_TEST_IDS.ARTICLE_INSTREAM}${adIndex}`}
              key={`pp-${ad.order}`}
              data-key={ad.order}
              adContent={ad.content}
              adTag={ad.adTag}
              position='in_stream'
              utmCampaign={utmCampaign}
              entityId={postId}
            />
          ),
          index: adIndex === 0 ? 1 : -1,
          node: adIndex === 0 ? 'p' : '',
        }));
      }
      return article.templateType === 'default' ? (
        <ArticlePage
          article={article}
          mostPopular={mostPopular}
          subdomain={Microsites.Education}
          sidebar={[
            <div
              key='fixed-content'
              style={{ display: 'contents' }}
            >
              {rightRailNavigation && <RightRailNavigation {...rightRailNavigation} />}
            </div>,
            adWidgets?.right_side?.[0] ? (
              <SidebarPaidPlacement key='sticky-content'>
                <PaidPlacement
                  dataCy={EDUCATION_ADS_TEST_IDS.ARTICLE_RIGHT_RAIL}
                  key={`pp-${adWidgets.right_side[0].order}`}
                  adContent={adWidgets.right_side[0].content}
                  adTag={adWidgets.right_side[0].adTag}
                  position='right_side'
                  utmCampaign={utmCampaign}
                  entityId={postId}
                />
              </SidebarPaidPlacement>
            ) : null,
          ]}
          adFrequency={0}
          embeds={articleEmbeds}
          headerAds={adWidgets?.header?.map((ad) => (
            <PaidPlacement
              dataCy={EDUCATION_ADS_TEST_IDS.ARTICLE_HEADER}
              key={`pp-${ad.order}`}
              adContent={ad.content}
              adTag={ad.adTag}
              position='header'
              utmCampaign={utmCampaign}
              entityId={postId}
            />
          ))}
        />
      ) : (
        <LandingPage
          article={article}
          mostPopular={mostPopular}
          subdomain={Microsites.Education}
          embeds={[]}
        />
      );
    }
    case PageType.EDU_CHILD_RANKING_LIST:
    case PageType.EDU_RANKING_LIST:
      return (
        <EduRankingPage
          rankingList={rankingList}
          adWidgets={adWidgets}
        />
      );
    default:
      return (
        <SchoolPage
          institution={institution}
          adWidgets={adWidgets}
        />
      );
  }
};

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { slug, section } = context.params as { slug: string[]; section: string };

    const educationDestinationData: MicrositeConfig = await safeAsync(
      () => getMicrositeConfig(Microsites.Education),
      recommendsFallback.recommendsData,
    );

    const disclosure: Disclosure | undefined | null = educationDestinationData.disclosure || null;

    const educationSections: SectionsConfig = educationDestinationData.sections as SectionsConfig;
    const sectionHub = (await safeAsync(
      () => getSection(Microsites.Education, 0, 0, Microsites.Education, educationSections),
      recommendsFallback.section,
    )) as Hub;

    let pageType;

    if (slug.length === 1) {
      pageType = PageType.ARTICLE;
      // the article sections have a new "type" property in the destination config with the value "articlesSection"
      if (educationSections[section]?.type !== 'articlesSection') {
        const isRankingList = await checkEduRankingListBySlug(slug[0], section);
        if (isRankingList) pageType = PageType.EDU_RANKING_LIST;
        else pageType = PageType.EDU_SCHOOL;
      }
    } else pageType = PageType.EDU_CHILD_RANKING_LIST;

    let hasArcherAds = false;
    let widgets = null;
    let widgetCss = null;
    let pageProps = {};

    // ARTICLE PAGE PROPS
    if (pageType === PageType.ARTICLE) {
      const commonProps = await getCommonProps({
        microsite: Microsites.Education,
        preview: context.preview,
        previewData: context.previewData as PreviewData,
        urlParams: { section, slug: slug[0] },
      });
      await safeAsync(() => populateThumbnailToMenuItems(commonProps.headerNav.topicNavigation), null);
      const rightRailNavigation = safe(
        () => parseHeaderForRightRailNavigation(commonProps.headerNav, '/education/'),
        null,
      );

      const hasNoAdsTag = !!commonProps.article.adTags.find((tag) => tag.slug === AD_TAGS.NO_ADS);

      if (!hasNoAdsTag) {
        const adTag = commonProps.article.adTags.find((tag) => (Object.values(AD_TAGS) as string[]).includes(tag.slug));
        const adTagSlug = adTag ? adTag.slug : AD_TAGS.DEFAULT;

        ({ hasArcherAds, widgets, widgetCss } = await safeAsync(
          () => getAdWigetsByAdTag(adTagSlug),
          recommendsFallback.adWidgets,
        ));
        if (widgetCss) {
          // Append ads widgetCss to any existing article widgetCss
          commonProps.headData.widgetCss = `
            ${commonProps.headData.widgetCss ? commonProps.headData.widgetCss : ''}
            ${widgetCss}
          `;
        }
      }
      pageProps = {
        ...commonProps,
        adWidgets: widgets,
        educationData: educationDestinationData,
        hasArcherAds,
        headerNav: {
          ...commonProps.headerNav,
          disclosure,
          progressBar: true,
          templateType: commonProps.article.templateType,
        },
        rightRailNavigation,
        section: sectionHub,
      };
    }
    // RANKING LIST AND INSTITUTION PROPS
    else {
      let rankingList = null;
      let institution = null;
      let metaData: EduRankingPageMeta | (Page & { prefixedDatabaseId: string }) | null = null;
      let breadcrumbs: Breadcrumb[] = null;

      const footer = await safeAsync(() => getFooter(Microsites.Recommends), fallback.footer);

      const menu: HeaderNav = await safeAsync(
        () =>
          process.env.FEATURE_FLAG_DISABLE_RECOMMENDS
            ? getMenu(
                'LEFT_MENU_COM',
                'RIGHT_MENU_COM',
                'TOPIC_NAVIGATION_COM',
                Microsites.Recommends,
                null,
                'RIGHT_MENU_COM',
              )
            : getMenu(
                'LEFT_MENU_RECOMMENDS',
                'LEFT_MENU_COM',
                'TOPIC_NAVIGATION_RECOMMENDS_V2',
                Microsites.Recommends,
                recommendsLogo,
                'RIGHT_MENU_COM',
              ),
        recommendsFallback.headerNav,
      );

      await safeAsync(() => populateThumbnailToMenuItems(menu.topicNavigation), null);

      let adTagSlug: string | null = '';
      if (pageType === PageType.EDU_RANKING_LIST) {
        ({ metaData, rankingList } = await getEduRankingListBySlug(slug, section));
        breadcrumbs = rankingList.breadcrumbs.items;
        adTagSlug = rankingList?.adTag;
      } else if (pageType === PageType.EDU_CHILD_RANKING_LIST) {
        ({ metaData, rankingList } = await getEduRankingListBySlug(slug, section, true));
        breadcrumbs = rankingList.breadcrumbs.items;
        adTagSlug = rankingList?.adTag;
      } else {
        ({ metaData, institution } = await getEduInstitutionBySlug(slug[0], section));
        breadcrumbs = institution.breadcrumbs.items;
        adTagSlug = institution?.adTag ? institution?.adTag : AD_TAGS.DEFAULT_SCHOOL;
      }

      if (adTagSlug) {
        ({ hasArcherAds, widgets, widgetCss } = await safeAsync(
          () => getAdWigetsByAdTag(adTagSlug!),
          recommendsFallback.adWidgets,
        ));
        if (widgetCss) {
          metaData.widgetCss = widgetCss;
        }
      }
      const pageAdConfig = setEducationPageAdConfig(sectionHub);

      pageProps = {
        adWidgets: widgets,
        dataLayer: safe(
          () => setPageGtmConfig({ ...metaData, platformCD: Microsites.Education } as GtmPage),
          fallback.dataLayer,
        ),
        footer,
        hasArcherAds,
        headData: safe(
          () => setHeadData({ ...metaData!, breadcrumbs: { items: breadcrumbs! } }, true, 'Fortune Education'),
          fallback.headData,
        ),
        headerNav: { ...menu, disclosure },
        institution,
        pageAdConfig,
        rankingList,
        section: sectionHub,
      };
    }
    const pianoPageConfig = setPianoConfig({ pageType: PianoExperiencePageType.Default, pageData: {} });

    return {
      props: {
        pageType,
        pianoPageConfig,
        ...pageProps,
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [],
    };
  }

  const paths = await getArticlePaths(Microsites.Education);

  return {
    fallback: 'blocking',
    paths,
  };
};

export default EduArticleAndRankingListAndSchool;
